import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  AlertCircle,
  CheckCircle,
  Globe,
  BarChart3,
  ArrowLeft,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { BASE_URL } from '../config';

export default function SEO({ onBack, onNavigate }) {
  const navigate = useNavigate(); // Add direct navigation as backup
  const [seoContent, setSeoContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);

  // Page choices from the model
  const pageChoices = [
    { value: 'home', label: 'Home Page' },
    { value: 'features', label: 'Features Page' },
    { value: 'pricing', label: 'Pricing Page' },
    { value: 'blog', label: 'Blog Page' },
    { value: 'contact', label: 'Contact Page' },
    { value: 'about', label: 'About Us' },
    { value: 'book-doctor', label: 'Book Doctor' },
    { value: 'doctors', label: 'Doctors' },
    { value: 'hospitals', label: 'Hospitals' },
    { value: 'appointments', label: 'Appointments' },
    { value: 'chat', label: 'Chat' },
    { value: 'ai-chat', label: 'AI Chat' },
    { value: 'ai-assistant', label: 'AI Assistant' }
  ];

  // Fetch SEO content
  const fetchSeoContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found', 'error');
        setSeoContent([]);
        return;
      }

      console.log('🔍 Fetching SEO content from:', `${BASE_URL}/api/seo/admin/list/`);
      
      const response = await fetch(`${BASE_URL}/api/seo/admin/list/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ SEO data received:', data);
        setSeoContent(data.seo_content || []);
        
        // Show success message only if there's data
        if (data.seo_content && data.seo_content.length > 0) {
          console.log(`📊 Loaded ${data.seo_content.length} SEO content items`);
        } else {
          console.log('📝 No SEO content found - this is normal for a new system');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API Error:', response.status, errorData);
        
        if (response.status === 401 || response.status === 403) {
          showNotification('Authentication failed. Please login again.', 'error');
        } else {
          showNotification(errorData.error || `Failed to fetch SEO content (${response.status})`, 'error');
        }
        setSeoContent([]);
      }
    } catch (error) {
      console.error('❌ Network Error fetching SEO content:', error);
      showNotification('Network error. Please check your connection.', 'error');
      setSeoContent([]);
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handle delete
  const handleDelete = async (seoId) => {
    if (!window.confirm('Are you sure you want to delete this SEO content?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found', 'error');
        return;
      }

      console.log('🗑️ Deleting SEO content:', seoId);

      const response = await fetch(`${BASE_URL}/api/seo/admin/${seoId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Delete response status:', response.status);

      if (response.ok) {
        showNotification('SEO content deleted successfully');
        fetchSeoContent();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Delete Error:', response.status, errorData);
        showNotification(errorData.error || `Failed to delete SEO content (${response.status})`, 'error');
      }
    } catch (error) {
      console.error('❌ Network Error deleting SEO content:', error);
      showNotification('Network error. Please check your connection.', 'error');
    }
  };

  // Open modal
  const openModal = (mode, seo = null) => {
    console.log('🔍 openModal called with:', { mode, seo, onNavigate: !!onNavigate });
    
    // Try onNavigate first, fallback to direct navigate
    const navigateFunction = onNavigate || navigate;
    
    if (!navigateFunction) {
      console.error('❌ No navigation function available');
      showNotification('Navigation error - please refresh the page', 'error');
      return;
    }

    if (mode === 'create') {
      console.log('🚀 Navigating to seo_create');
      if (onNavigate) {
        onNavigate('seo_create');
      } else {
        navigate('/seo_create');
      }
    } else if (mode === 'edit') {
      console.log('🚀 Navigating to seo_edit with ID:', seo.id);
      if (onNavigate) {
        onNavigate(`seo_edit/${seo.id}`);
      } else {
        navigate(`/seo_edit/${seo.id}`);
      }
    } else if (mode === 'view') {
      console.log('🚀 Navigating to seo_view with ID:', seo.id);
      if (onNavigate) {
        onNavigate(`seo_view/${seo.id}`);
      } else {
        navigate(`/seo_view/${seo.id}`);
      }
    }
  };

  // Get SEO score color
  const getSeoScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Filter SEO content
  const filteredSeoContent = seoContent.filter(seo =>
    seo.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    seo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    seo.page_key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchSeoContent();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-800">SEO Management</h1>
            <p className="text-slate-600">Manage SEO content for all pages</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              console.log('🔍 Blog Management button clicked');
              if (onNavigate) {
                onNavigate('blog');
              } else {
                navigate('/blog');
              }
            }}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Blog Management
          </button>
          <button
            onClick={() => {
              console.log('🔍 FAQ Management button clicked');
              if (onNavigate) {
                onNavigate('faq');
              } else {
                navigate('/faq');
              }
            }}
            className="bg-purple-900 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            {/* <HelpCircle className="w-4 h-4" /> */}
            FAQ Management
          </button>
          {/* <button
            onClick={() => {
              console.log('🔍 Add SEO Content button clicked');
              openModal('create');
            }}
            className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add SEO Content
          </button> */}
        </div>
        
        {/* Debug button - remove after testing */}
        {/* <button
          onClick={() => {
            console.log('🧪 Debug button clicked');
            console.log('🧪 onNavigate available:', !!onNavigate);
            if (onNavigate) {
              onNavigate('seo_create');
            } else {
              alert('onNavigate not available');
            }
          }}
          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
        >
          Debug Test
        </button> */}
      </div>

      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`p-4 rounded-lg flex items-center gap-2 ${
              notification.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search SEO content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
          />
        </div>
        <button
            onClick={() => {
              console.log('🔍 Add SEO Content button clicked');
              openModal('create');
            }}
            className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add SEO Content
          </button>
      </div>


      {/* SEO Content List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-medical-500 mx-auto"></div>
            <p className="text-slate-600 mt-2">Loading SEO content...</p>
          </div>
        ) : filteredSeoContent.length === 0 ? (
          <div className="p-8 text-center">
            <Globe className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800 mb-2">No SEO Content Found</h3>
            <p className="text-slate-600 mb-4">
              {searchTerm 
                ? `No SEO content matches "${searchTerm}"`
                : "Get started by creating your first SEO content"
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  console.log('🔍 Empty state Create button clicked');
                  openModal('create');
                }}
                className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Create SEO Content
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Page
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Title
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    SEO Score
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredSeoContent.map((seo) => (
                  <motion.tr
                    key={seo.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{seo.page_name}</div>
                        <div className="text-sm text-slate-500">{seo.page_key}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900 max-w-xs truncate" title={seo.title}>
                        {seo.title}
                      </div>
                      <div className="text-xs text-slate-500">
                        {seo.title_length} chars
                      </div>
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeoScoreColor(seo.seo_score || 0)}`}>
                        <BarChart3 className="w-3 h-3 mr-1" />
                        {seo.seo_score || 0}/100
                      </span>
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        seo.is_active 
                          ? 'text-green-800 bg-green-100' 
                          : 'text-red-800 bg-red-100'
                      }`}>
                        {seo.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {new Date(seo.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openModal('view', seo)}
                          className="text-slate-600 hover:text-slate-900"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openModal('edit', seo)}
                          className="text-medical-600 hover:text-medical-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(seo.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}